import { hasElements } from "utils/arrays";
import ClientFactory from "./ClientFactory";

function processError(error, callback) {
    const { response } = error;
    if (response && response.data) {
        callback(response.data);
    } else {
        callback("Network error!");
    }
}

const ApiFactory = (clientName, clientParams) => {
    const Client = ClientFactory.getClient(clientName, clientParams);

    return {
        getAll: async ({ url, onSuccess, onError, normalizer = (data) => data, data }) => {
            try {
                const response = await Client.get(data ? { url, data } : { url });
                if (response && hasElements(response.data)) {
                    onSuccess(normalizer(response.data));
                } else {
                    onSuccess([]);
                }
            } catch (error) {
                processError(error, onError);
            }
        },
        getList: async ({ url, data, onSuccess, onInfo, onError, normalizer = (data) => data }) => {
            try {
                const response = await Client.get({ url, data });
                const { data: responseData } = response || {};
                let newContent = [];
                let pageableData;
                if (responseData) {
                    const { content, ...pageable } = responseData;
                    if (hasElements(content)) {
                        newContent = normalizer(content);
                    }
                    pageableData = pageable;
                }
                if (hasElements(newContent)) {
                    onSuccess(newContent, pageableData);
                } else {
                    onSuccess([]);
                    onInfo("No results to display!", pageableData);
                }
            } catch (error) {
                processError(error, onError);
            }
        },
        add: async ({ url, data, onSuccess, onError }) => {
            try {
                const response = await Client.post({ url, data });
                onSuccess(response.data);
            } catch (error) {
                processError(error, onError);
            }
        },
        addAsync: async ({ url, data, config }) => {
            try {
                return await Client.post({ url, data, config });
            } catch (error) {
                throw error;
            }
        },
        update: async ({ url, data, onSuccess, onError }) => {
            try {
                const response = await Client.put({ url, data });
                onSuccess(response.data);
            } catch (error) {
                processError(error, onError);
            }
        },
        updateAsync: async ({ url, data }) => {
            try {
                return await Client.put({ url, data });
            } catch (error) {
                throw error;
            }
        },
        deleteAll: async ({ url, data, onSuccess, onError }) => {
            try {
                const response = await Client.deleteAll({ url, data });
                onSuccess(response.data);
            } catch (error) {
                processError(error, onError);
            }
        },
        deleteAllAsync: async ({ url, data }) => {
            try {
                return await Client.deleteAll({ url, data });
            } catch (error) {
                throw error;
            }
        },
        getOneById: async ({ url, onSuccess, onError, normalizer = (data) => data }) => {
            try {
                const response = await Client.get({ url });
                onSuccess(normalizer(response.data));
            } catch (error) {
                processError(error, onError);
            }
        },
        getListAsync: async ({ url, data, normalizer = (data) => data }) => {
            try {
                const response = await Client.get({ url, data });
                const { data: responseData } = response || {};
                let newContent = [];
                let pageableData;
                if (responseData) {
                    const { content, ...pageable } = responseData;
                    if (hasElements(content)) {
                        newContent = normalizer(content);
                    }
                    pageableData = pageable;
                }
                return { data: newContent, pageableData };
            } catch (error) {
                return null;
            }
        },
        getAsync: async ({ url, normalizer = (data) => data, data }) => {
            try {
                const response = await Client.get({ url, data });
                return normalizer(response.data);
            } catch (error) {
                return null;
            }
        },
        getAsyncWithThrow: async ({ url, normalizer = (data) => data, data }) => {
            try {
                const response = await Client.get({ url, data });
                return normalizer(response.data);
            } catch (error) {
                throw error;
            }
        },
        getAllAsync: async ({ url, normalizer = (data) => data, data }) => {
            try {
                const response = await Client.get({ url, data });
                return normalizer(response.data);
            } catch (error) {
                throw error;
            }
        },
        patch: async ({ url, data, onSuccess, onError }) => {
            try {
                const response = await Client.patch({ url, data });
                onSuccess(response.data);
            } catch (error) {
                processError(error, onError);
            }
        },
        patchAsync: async ({ url, data, config }) => {
            try {
                return await Client.patch({ url, data, config });
            } catch (error) {
                throw error;
            }
        },
    };
};

export default ApiFactory;
